/**
 * Google reCAPTCHA v3 – client-side bot protection
 *
 * Layers of protection (applied in order at submit time)
 * ──────────────────────────────────────────────────────
 * 1. Honeypot       – silent block if a hidden field was filled (bots fill all fields)
 * 2. Minimum time   – block if submitted < 3 s after page load (bots submit instantly)
 * 3. Server verify  – submit is intercepted, a fresh token is generated and sent to
 *                     the verify endpoint; the form is only submitted when the
 *                     endpoint confirms success and the score meets the threshold.
 *
 * Layers 1–2 are always active, even if reCAPTCHA fails to load.
 * Layer 3 activates when window.recaptchaSiteKey and window.recaptchaVerifyUrl are
 * both set, and FAILS CLOSED: if the token can't be generated or the endpoint
 * rejects it / can't be reached, the submission is blocked with an error message
 * and the user can retry.
 *
 * If no verify URL is configured, the token is still generated and injected but
 * the form submits without server-side validation.
 *
 * Configuration (Theme Settings → Google reCAPTCHA)
 * ──────────────────────────────────────────────────
 *   window.recaptchaSiteKey        — reCAPTCHA v3 site key (required)
 *   window.recaptchaVerifyUrl      — Cloudflare Worker verify endpoint (optional)
 *   window.recaptchaScoreThreshold — minimum score 0.0–1.0, 0 = disabled (optional)
 */

(function () {
  'use strict';

  var MIN_FILL_MS   = 3000;  // minimum ms a human needs to read + fill a form
  var GRECAPTCHA_TIMEOUT_MS = 15000;
  var HONEYPOT_NAME = 'contact[website]';
  var ERROR_CLASS   = 'recaptcha-score-error';
  var PAGE_LOAD_TS  = Date.now();

  var FORMS = {
    '#ContactForm'         : 'contact',
    '#NewsletterPopupForm' : 'newsletter_popup',
    '.newsletter-form'     : 'newsletter'
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  function getAction(form) {
    for (var sel in FORMS) {
      if (form.matches(sel)) return FORMS[sel];
    }
    return null;
  }

  function injectToken(form, token) {
    // Use a custom field name instead of 'g-recaptcha-response'.
    // Shopify's legacy backend intercepts g-recaptcha-response and attempts
    // v2 verification on it, returning "v2 is no longer supported".
    var input = form.querySelector('input[name="rcaptcha_token"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'rcaptcha_token';
      form.appendChild(input);
    }
    input.value = token;
  }

  function addHoneypot(form) {
    if (form.querySelector('input[name="' + HONEYPOT_NAME + '"]')) return;
    var hp = document.createElement('input');
    hp.type         = 'text';
    hp.name         = HONEYPOT_NAME;
    hp.tabIndex     = -1;
    hp.autocomplete = 'off';
    hp.setAttribute('aria-hidden', 'true');
    hp.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
    form.appendChild(hp);
  }

  function showError(form, message) {
    var btn = form.querySelector('[type="submit"]');
    var err = form.querySelector('.' + ERROR_CLASS);
    if (!err && btn) {
      err = document.createElement('p');
      err.className = ERROR_CLASS;
      err.style.cssText = 'color:#c00;font-size:0.9em;margin:8px 0 0;';
      btn.parentNode.insertBefore(err, btn);
    }
    if (err) err.textContent = message;
  }

  function clearError(form) {
    var err = form.querySelector('.' + ERROR_CLASS);
    if (err) err.parentNode.removeChild(err);
  }

  function setProcessing(form, on) {
    var btn = form.querySelector('[type="submit"]');
    form.dataset.recaptchaProcessing = on ? 'true' : '';
    if (btn) btn.disabled = !!on;
  }

  // ── grecaptcha readiness (api.js is loaded with defer; poll until present) ─

  var readyPromise = null;

  function whenGrecaptchaReady() {
    if (!readyPromise) {
      readyPromise = new Promise(function (resolve, reject) {
        var timedOut = false;
        var timeout = setTimeout(function () {
          timedOut = true;
          readyPromise = null; // allow a retry on the next submit
          reject(new Error('reCAPTCHA failed to load'));
        }, GRECAPTCHA_TIMEOUT_MS);

        (function poll() {
          if (timedOut) return;
          if (typeof grecaptcha !== 'undefined' && grecaptcha.ready) {
            grecaptcha.ready(function () {
              clearTimeout(timeout);
              resolve();
            });
            return;
          }
          setTimeout(poll, 100);
        })();
      });
    }
    return readyPromise;
  }

  // ── Submit-time token generation + server validation (fail closed) ───────

  function verifyToken(token, action) {
    return fetch(window.recaptchaVerifyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token, action: action })
    }).then(function (r) { return r.json(); });
  }

  function validateAndSubmit(form, action) {
    setProcessing(form, true);

    whenGrecaptchaReady()
      .then(function () {
        return grecaptcha.execute(window.recaptchaSiteKey, { action: action });
      })
      .then(function (token) {
        injectToken(form, token);

        // No verify endpoint configured → token-only mode, submit natively
        if (!window.recaptchaVerifyUrl) return null;

        return verifyToken(token, action).then(function (d) {
          if (!d.success) {
            console.warn('[reCAPTCHA] verify failed | error-codes:', d['error-codes']);
            throw new Error('verification-failed');
          }

          var threshold = parseFloat(window.recaptchaScoreThreshold) || 0;
          var pass = threshold <= 0 || d.score >= threshold;

          console.log(
            '[reCAPTCHA] score=' + d.score +
            (pass ? ' ✅' : ' ⚠️  BLOCKED') +
            ' | threshold=' + threshold +
            ' | action=' + d.action
          );

          if (!pass) throw new Error('low-score');
          return d;
        });
      })
      .then(function () {
        clearError(form);
        setProcessing(form, false);
        submitValidated(form);
      })
      .catch(function (err) {
        console.warn('[reCAPTCHA] blocked at submit:', err && err.message);
        setProcessing(form, false);
        showError(form, 'Security verification failed. Please try again.');
      });
  }

  function submitValidated(form) {
    // Re-dispatch through requestSubmit() so other submit handlers on the form
    // (e.g. the newsletter popup's success UI) still run; the capture-phase
    // gate below lets it through via the data flag.
    form.dataset.recaptchaValidated = 'true';
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  // ── Step 1: Add honeypots immediately (defer = DOM is ready) ─────────────
  // Done before any reCAPTCHA work so forms are protected even if the
  // reCAPTCHA script is slow or blocked.

  (function seedHoneypots() {
    var seen = [];
    for (var sel in FORMS) {
      document.querySelectorAll(sel).forEach(function (form) {
        if (seen.indexOf(form) !== -1) return; // deduplicate
        seen.push(form);
        addHoneypot(form);
      });
    }
  })();

  // ── Step 2: Submit gate (capture phase) ──────────────────────────────────
  // Runs regardless of reCAPTCHA load status.
  // Capture phase fires before bubbling and before any element-level handlers.

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var action = getAction(form);
    if (!action) return;

    // Already validated → let the native submission (and other handlers) run
    if (form.dataset.recaptchaValidated === 'true') {
      form.dataset.recaptchaValidated = '';
      return;
    }

    // Layer 1 — Honeypot (silent: don't reveal what failed to the bot)
    var hp = form.querySelector('input[name="' + HONEYPOT_NAME + '"]');
    if (hp && hp.value.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // Layer 2 — Minimum time on page
    if (Date.now() - PAGE_LOAD_TS < MIN_FILL_MS) {
      e.preventDefault();
      e.stopPropagation();
      showError(form, 'Please take a moment to complete the form before submitting.');
      return;
    }

    // Layer 3 — Intercept and validate against the verify endpoint
    if (window.recaptchaSiteKey) {
      e.preventDefault();
      e.stopPropagation();

      if (form.dataset.recaptchaProcessing === 'true') return;

      clearError(form);
      validateAndSubmit(form, action);
    }

    // No site key configured → native submission, honeypot + timing only

  }, true /* capture phase */);

  // ── Step 3: Show the badge on pages with a protected form ────────────────

  if (window.recaptchaSiteKey) {
    var formsFound = 0;
    for (var selector in FORMS) {
      formsFound += document.querySelectorAll(selector).length;
    }
    if (formsFound > 0) {
      var badge = document.querySelector('.rcfb');
      if (badge) badge.style.display = 'flex';
    }
  }

})();
